#!/usr/bin/env node
import * as p from '@clack/prompts';
import * as color from 'picocolors';
import { spawn, execSync } from 'child_process';
import { Command } from 'commander';
import logUpdate from 'log-update';
import fs from 'fs';
import path from 'path';

import { serverContent, envContent, gitignoreContent as backendGitignoreContent, packageJsonContent as backendPackageJsonContent, prismaSchemaContent, tsConfigContent } from './template/backend';
import { pageContent, layoutContent, globalsCssContent, nextConfigContent, nextEnvContent, packageJsonContent as frontendPackageJsonContent, eslintConfigContent, gitignoreContent, postcssConfigContent, tsConfigFrontendContent } from './template/frontend';
import { packageJsonContent, readmeContent, rootEnvContent, rootGitignore } from './template/root';
import { displayNextpressLogo } from './utils/logo';

const version = '2.8.1';



type PackageManager = 'npm' | 'pnpm' | 'yarn' | 'bun';

interface ProjectConfig {
  name: string;
  path: string;
  packageManager: PackageManager;
}

function getPackageManagerCommands(packageManager: PackageManager) {
  return {
    install: packageManager === 'npm' ? 'npm install' :
      packageManager === 'pnpm' ? 'pnpm install' :
        packageManager === 'yarn' ? 'yarn' :
          'bun install',
    dev: packageManager === 'npm' ? 'npm run dev' :
      packageManager === 'pnpm' ? 'pnpm dev' :
        packageManager === 'yarn' ? 'yarn dev' :
          'bun dev',
    build: packageManager === 'npm' ? 'npm run build' :
      packageManager === 'pnpm' ? 'pnpm build' :
        packageManager === 'yarn' ? 'yarn build' :
          'bun build',
    start: packageManager === 'npm' ? 'npm run start' :
      packageManager === 'pnpm' ? 'pnpm start' :
        packageManager === 'yarn' ? 'yarn start' :
          'bun start',
    prismaGenerate: packageManager === 'npm' ? 'npm run prisma:generate' :
      packageManager === 'pnpm' ? 'pnpm prisma:generate' :
        packageManager === 'yarn' ? 'yarn prisma:generate' :
          'bun prisma:generate',
    prismaMigrate: packageManager === 'npm' ? 'npm run prisma:migrate' :
      packageManager === 'pnpm' ? 'pnpm prisma:migrate' :
        packageManager === 'yarn' ? 'yarn prisma:migrate' :
          'bun prisma:migrate'
  };
}

async function isPackageManagerInstalled(pm: PackageManager): Promise<boolean> {
  try {
    execSync(`${pm} --version`, { stdio: 'ignore' });
    return true;
  } catch (error) {
    return false;
  }
}

function validateProjectName(name: string): string | undefined {
  name = name.toLowerCase();
  if (!/^[a-z]/.test(name)) return 'Project name must start with a letter (a-z).';
  if (!/^[a-z0-9-]+$/.test(name)) return 'Project name can only contain lowercase letters, numbers, and hyphens (-).';
  if (!/[a-z0-9]$/.test(name)) return 'Project name must end with a letter or number.';
  return undefined;
}

async function executeStep(
  message: string,
  command: string,
  packageName: string,
  weight: number,
  showLogs = false,
  cwd?: string
): Promise<void> {
  progressState.currentPackage = packageName;
  return new Promise<void>((resolve, reject) => {
    const stdio = showLogs ? 'inherit' : 'ignore';
    let shellOption: string | boolean;
    if (process.platform === 'win32') {
      shellOption = 'cmd.exe';
    } else if (process.env.SHELL && fs.existsSync(process.env.SHELL)) {
      shellOption = process.env.SHELL;
    } else {
      shellOption = true;
    }
    const options: any = {
      shell: shellOption,
      stdio,
      cwd: cwd ? path.resolve(cwd) : process.cwd(),
      windowsHide: true
    };

    const child = spawn(command, options);
    child.on('close', (code) => {
      if (code === 0) {
        progressState.totalCompleted++;
        progressState.percentage += weight;
        progressState.logs.push({ message: `${message} completed`, success: true });
        updateProgressDisplay();
        resolve();
      } else {
        progressState.logs.push({ message: `${message} failed`, success: false });
        updateProgressDisplay();
        reject(new Error(`${message} failed with code ${code}`));
      }
    });
    child.on('error', (error) => {
      progressState.logs.push({ message: `${message} failed: ${error.message}`, success: false });
      updateProgressDisplay();
      reject(error);
    });
  });
}

async function executeStepWithRetry(
  message: string,
  command: string,
  packageName: string,
  weight: number,
  showLogs = false,
  cwd?: string,
  retries: number = 3
): Promise<void> {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      await executeStep(message, command, packageName, weight, showLogs, cwd);
      return;
    } catch (error) {
      if (attempt === retries) {
        throw error;
      } else {
        progressState.logs.push({ message: `${message} failed, retrying (${attempt}/${retries})`, success: false });
        updateProgressDisplay();
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }
}

// Create root package.json file for the Project
function createRootPackageJson(projectPath: string, projectName: string, packageManager: PackageManager) {
  fs.writeFileSync(
    path.join(projectPath, 'package.json'),
    packageJsonContent(projectName, packageManager)
  );
}

// Create root .env file with DATABASE_URL
function createRootEnvFile(projectPath: string) {
  fs.writeFileSync(
    path.join(projectPath, '.env'),
    rootEnvContent
  );
}


function createRootGitignore(projectPath: string) {
  fs.writeFileSync(
    path.join(projectPath, '.gitignore'),
    rootGitignore
  );
}


// Create Prisma schema in server directory
function createPrismaSchema(serverPath: string) {
  const prismaPath = path.join(serverPath, 'prisma');
  if (!fs.existsSync(prismaPath)) {
    fs.mkdirSync(prismaPath, { recursive: true });
  }
  fs.writeFileSync(
    path.join(prismaPath, 'schema.prisma'),
    prismaSchemaContent
  );
}

// Create TypeScript configuration for the backend
function createTsConfig(backendPath: string) {
  fs.writeFileSync(
    path.join(backendPath, 'tsconfig.json'),
    tsConfigContent
  );
}

// Create Next.js frontend with content
function createNextJsFiles(frontendPath: string) {
  // Create necessary directories first
  if (!fs.existsSync(frontendPath)) {
    fs.mkdirSync(frontendPath, { recursive: true });
  }

  // Create src directory and app directory
  const srcPath = path.join(frontendPath, 'src');
  const appPath = path.join(srcPath, 'app');
  fs.mkdirSync(srcPath, { recursive: true });
  fs.mkdirSync(appPath, { recursive: true });

  // Write template files
  fs.writeFileSync(path.join(appPath, 'page.tsx'), pageContent);
  fs.writeFileSync(path.join(appPath, 'layout.tsx'), layoutContent);
  fs.writeFileSync(path.join(appPath, 'globals.css'), globalsCssContent);
  fs.writeFileSync(path.join(frontendPath, 'next.config.ts'), nextConfigContent);
  fs.writeFileSync(path.join(frontendPath, 'next-env.d.ts'), nextEnvContent);
  fs.writeFileSync(path.join(frontendPath, 'package.json'), frontendPackageJsonContent);
  fs.writeFileSync(path.join(frontendPath, 'eslint.config.mjs'), eslintConfigContent);
  fs.writeFileSync(path.join(frontendPath, '.gitignore'), gitignoreContent);
  fs.writeFileSync(path.join(frontendPath, 'postcss.config.mjs'), postcssConfigContent);
  fs.writeFileSync(path.join(frontendPath, 'tsconfig.json'), tsConfigFrontendContent);
}

// Create Express backend with content
function createExpressFiles(backendPath: string) {
  // Ensure the directory exists
  if (!fs.existsSync(backendPath)) {
    fs.mkdirSync(backendPath, { recursive: true });
  }

  // Create src directory
  const srcPath = path.join(backendPath, 'src');
  fs.mkdirSync(srcPath, { recursive: true });

  // Write server.ts with the template content
  fs.writeFileSync(path.join(srcPath, 'server.ts'), serverContent);

  // Create .env file
  fs.writeFileSync(path.join(backendPath, '.env'), envContent);

  // Create .gitignore file
  fs.writeFileSync(path.join(backendPath, '.gitignore'), backendGitignoreContent);

  // Create package.json for backend
  fs.writeFileSync(
    path.join(backendPath, 'package.json'),
    backendPackageJsonContent('server')
  );
}

// Create README file
function createReadmeFile(projectPath: string, projectName: string, packageManager: PackageManager) {
  fs.writeFileSync(
    path.join(projectPath, 'README.md'),
    readmeContent(projectName, packageManager)
  );
}

const progressState = {
  percentage: 0,
  currentPackage: '',
  elapsedTime: 0,
  totalCompleted: 0,
  totalPackages: 0,
  logs: [] as { message: string, success: boolean }[],
  isComplete: false
};

let progressInterval: NodeJS.Timeout;
let projectName = '';

function updateProgressDisplay() {
  const progressPercentage = Math.min(100, progressState.percentage);
  const progressBar = color.green('■').repeat(Math.floor(progressPercentage / 5)) +
    '□'.repeat(20 - Math.floor(progressPercentage / 5));
  const formattedTime = `${progressState.elapsedTime.toFixed(1)}s`;
  let header = '';

  if (progressState.isComplete) {
    header = `${color.green('✓')} Project ${color.cyan(projectName)} created successfully`;
  } else {
    const spinnerFrames = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'];
    const spinnerFrame = spinnerFrames[Math.floor(Date.now() / 100) % spinnerFrames.length];
    header = `${spinnerFrame} Creating project ${color.cyan(projectName)}`;
  }

  const progressLine = `${progressBar} ${color.bold(progressPercentage + '%')} | ${color.yellow(formattedTime)} | ${color.blue(progressState.currentPackage)} | ${color.green(progressState.totalCompleted + ' completed')}`;
  const logs = progressState.logs.map(log =>
    `${log.success ? color.green('✓') : color.red('✗')} ${color.dim(log.message)}`
  ).join('\n');

  logUpdate(`${header}\n${progressLine}\n${logs}`);
}

function startProgressTracking() {
  const startTime = Date.now();
  progressInterval = setInterval(() => {
    progressState.elapsedTime = (Date.now() - startTime) / 1000;
    updateProgressDisplay();
  }, 100);
}

function stopProgressTracking() {
  clearInterval(progressInterval);
  progressState.isComplete = true;
  updateProgressDisplay();
}

async function main(): Promise<void> {
  console.clear();
  displayNextpressLogo(version);
  p.intro(`${color.bgCyan(color.black(' Nextpress Project '))} ${color.dim(`create-nextpress v${version}`)}`);

  const program = new Command()
    .name('create-nextpress')
    .description('A CLI for creating Next.js + Express TypeScript projects')
    .version(`v${version}`, '-v, --version', 'Display the version number')
    .argument('[projectName]', 'Name of the project or use "." for current directory')
    .option('--debug', 'Show debug logs during installation')
    .parse(process.argv);

  const projectArg = program.args[0];
  const options = program.opts();
  const debugMode = options.debug || false;

  let projectPath = '';
  let projectName = '';

  if (projectArg !== undefined) {
    if (projectArg === ".") {
      projectPath = process.cwd();
      projectName = path.basename(projectPath);
      const errorMsg = validateProjectName(projectName);
      if (errorMsg) {
        p.cancel(`${color.red('✗')} ${errorMsg}`);
        process.exit(1);
      }
    } else {
      const errorMsg = validateProjectName(projectArg);
      if (errorMsg) {
        p.cancel(`${color.red('✗')} ${errorMsg}`);
        process.exit(1);
      }
      projectName = projectArg.toLowerCase();
      projectPath = path.join(process.cwd(), projectName);
      if (fs.existsSync(projectPath)) {
        p.cancel(`${color.red('✗')} A folder with the name "${projectName}" already exists at ${projectPath}.`);
        process.exit(1);
      }
    }
  } else {
    const name = await p.text({
      message: 'Enter project name',
      placeholder: 'my-nextpress',
      validate: validateProjectName,
      defaultValue: 'my-nextpress'
    });

    if (p.isCancel(name)) {
      p.cancel('Operation cancelled.');
      process.exit(0);
    }

    projectName = name;
    projectPath = path.join(process.cwd(), projectName);
    if (fs.existsSync(projectPath)) {
      p.cancel(`${color.red('✗')} A folder with the name "${projectName}" already exists.`);
      process.exit(1);
    }
  }

  // Ask for package manager
  const availablePms: { value: PackageManager, label: string, hint?: string }[] = [
    { value: 'npm', label: 'npm' },
    { value: 'pnpm', label: 'pnpm' },
    { value: 'yarn', label: 'yarn' },
    { value: 'bun', label: 'bun' }
  ];

  const pmOptions = await Promise.all(availablePms.map(async (pm) => {
    const installed = await isPackageManagerInstalled(pm.value);
    if (!installed) {
      return { ...pm, hint: color.yellow('Not detected on your system') };
    }
    return pm;
  }));

  const packageManager = await p.select({
    message: 'Select package manager',
    options: pmOptions,
    initialValue: 'npm'
  });

  if (p.isCancel(packageManager)) {
    p.cancel('Operation cancelled.');
    process.exit(0);
  }

  const selectedPm = packageManager as PackageManager;

  if (!await isPackageManagerInstalled(selectedPm)) {
    p.note(`${color.yellow('Warning:')} ${selectedPm} does not seem to be installed or is not in your PATH.\n         Please ensure it is installed to be able to run project commands.`, color.yellow('Heads up!'));
  }

  const config: ProjectConfig = {
    name: projectName,
    path: projectPath,
    packageManager: selectedPm
  };

  // Create main project directory if it doesn't exist
  if (!fs.existsSync(projectPath)) {
    fs.mkdirSync(projectPath, { recursive: true });
  }

  // If pnpm is selected, create the pnpm-workspace.yaml file
  if (config.packageManager === 'pnpm') {
    fs.writeFileSync(
      path.join(projectPath, 'pnpm-workspace.yaml'),
      `packages:
  - apps/client
  - apps/server`
    );
  }

  // Define paths for the new project structure
  const appsPath = path.join(projectPath, 'apps');
  const clientPath = path.join(appsPath, 'client');
  const serverPath = path.join(appsPath, 'server');

  progressState.totalPackages = 5; // Root .env + Next.js Client + Express Server + Prisma in Server + Root Setup
  progressState.logs = [];
  startProgressTracking();
  const startTime = Date.now();

  try {
    // Create root directory structure
    fs.mkdirSync(appsPath, { recursive: true });
    fs.mkdirSync(clientPath, { recursive: true });
    fs.mkdirSync(serverPath, { recursive: true });

    // Create root package.json
    createRootPackageJson(projectPath, projectName, config.packageManager);
    await executeStepWithRetry(
      'Creating root package.json',
      'echo "Root package.json created"',
      'root',
      10,
      debugMode
    );

    // Create root .env file
    createRootEnvFile(projectPath);
    await executeStepWithRetry(
      'Creating root .env file',
      'echo "Root .env file created"',
      'root-env',
      10,
      debugMode
    );


// Create .gitignore file in root directory
createRootGitignore(projectPath);
await executeStepWithRetry(
  'Creating root .gitignore file',
  'echo "Root .gitignore file created"',
  'root-gitignore',
  10,
  debugMode
);



    // Create frontend in apps/client
    createNextJsFiles(clientPath);
    await executeStepWithRetry(
      'Creating Next.js frontend',
      'echo "Frontend structure created"',
      'client',
      30,
      debugMode
    );

    // Create backend in apps/server
    createExpressFiles(serverPath);
    await executeStepWithRetry(
      'Creating Express backend',
      'echo "Backend structure created"',
      'server',
      20,
      debugMode
    );

    // Create Prisma schema in server directory
    createPrismaSchema(serverPath);
    await executeStepWithRetry(
      'Creating Prisma schema in server',
      'echo "Prisma schema created in server directory"',
      'server-prisma',
      10,
      debugMode
    );

    // Create README
    createReadmeFile(projectPath, projectName, config.packageManager);
    await executeStepWithRetry(
      'Creating README',
      'echo "README created"',
      'docs',
      10,
      debugMode
    );

    // Create TypeScript configuration for backend
    createTsConfig(serverPath);
    await executeStepWithRetry(
      'Creating TypeScript configuration',
      'echo "TypeScript configuration created"',
      'tsconfig',
      10,
      debugMode
    );

    stopProgressTracking();
    const endTime = Date.now();
    const executionTime = ((endTime - startTime) / 1000).toFixed(2);

    const commands = getPackageManagerCommands(config.packageManager);

    const summaryMessage = `
${color.bold(color.cyan('🚀 Project structure created successfully!'))}

${color.bold('📦 Project Structure:')}
${color.cyan('➤')} ${projectName}/
${color.cyan('  ├─')} .env       (Root environment variables)
${color.cyan('  ├─')} apps/
${color.cyan('  │   ├─')} client/   (Next.js core client)
${color.cyan('  │   └─')} server/   (Express core server)
${color.cyan('  │       └─')} prisma/   (Prisma schema)
${color.cyan('  └─')} package.json (Root package.json)

${color.bold('📦 Added dependencies (not yet installed):')}
${color.cyan('➤')} Client: Next.js
${color.cyan('➤')} Server: Express, Cors, Dotenv
${color.cyan('➤')} Server: Prisma Schema

${color.bold('🔰 Next steps:')}
${color.cyan(`$ cd ${path.basename(projectPath)}`)}
${color.cyan(`$ ${commands.install}        # Install all dependencies`)}
${color.cyan(`$ ${commands.dev}        # Start both frontend and backend`)}

${color.bold(`⚠️ IMPORTANT: You must run '${commands.install}' to complete the setup.`)}

${color.bold(`⏱️ Total setup time: ${color.green(executionTime + ' seconds')}`)}
`;
    p.note(summaryMessage, `${color.green('✓')} ${color.bold('Project structure ready')}`);
  } catch (error) {
    stopProgressTracking();
    p.cancel(`${color.red('✗')} Installation failed. See logs for details.`);
    console.error(error);
    process.exit(1);
  }
}

main();